import { MenuActionItem } from '@creditornot/cb-menu';
import { SelectAsync, SelectAsyncProps } from '@creditornot/cb-select';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import * as merchantApi from 'apis/restaurant-api/merchant-admin/merchant/api';
import * as userApi from 'apis/restaurant-api/merchant-admin/user/api';
import { SearchUser } from 'apis/restaurant-api/merchant-admin/user/types';
import * as venueApi from 'apis/restaurant-api/merchant-admin/venue/api';

import { EntityType } from './types';

type Props = {
  entityId: string;
  entityType: EntityType;
  onChange: (user: User) => void;
} & Pick<SelectAsyncProps<User>, 'menuPortalTarget'>;

export type User = {
  email: string;
  isEmailVerified: boolean;
  label: string;
  value: string;
};

const getAccessQueryKey = (entityId: string): string =>
  ({
    merchant: `/v1/merchant-admin/merchants/${entityId}/access`,
    venue: `/v1/merchant-admin/venues/${entityId}/access`,
    brand: `/v1/merchant-admin/brands/${entityId}/access`,
    enterprise: `/v1/merchant-admin/enterprises/${entityId}/access`,
  })[entityId] ?? '';

const getUserDisplayName = (user: SearchUser | null) => {
  return `${user?.name.first_name ?? ''} ${user?.name.last_name ?? ''}`.trim();
};

const UserSelect = ({ entityId, entityType, menuPortalTarget, onChange }: Props) => {
  const intl = useIntl();

  const [value, setValue] = useState<string | null>(null);

  const { data: roles = [] } = useQuery(
    [getAccessQueryKey(entityId)],
    async () => {
      return entityType === 'merchant' && entityId !== null
        ? merchantApi.fetchMerchantAccess(entityId)
        : entityType === 'venue' && entityId !== null
          ? venueApi.fetchVenueAccess(entityId)
          : [];
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const loadOptions = async (value: string): Promise<User[]> => {
    const { users } = await userApi.searchUsers(value);
    return users
      .filter(user => !roles.map(role => role.id).some(id => user.id === id))
      .map(user => {
        return {
          email: user.email,
          isEmailVerified: user.email_verified,
          label: getUserDisplayName(user),
          value: user.id,
        };
      });
  };

  return (
    <SelectAsync<User>
      autoFocus
      loadOptions={loadOptions}
      menuPortalTarget={menuPortalTarget}
      onChange={setValue}
      optionComponent={({ option, innerRef, onClick, ...rest }) => (
        <MenuActionItem
          ref={innerRef}
          subLabel={option.email}
          variant="black"
          onClick={event => {
            onClick(event);
            onChange(option);
          }}
          {...rest}
        >
          {option.label}
        </MenuActionItem>
      )}
      placeholder={intl.formatMessage({ id: 'userManagement.userSearchPlaceholder' })}
      value={value}
    />
  );
};

export default UserSelect;
